import { useState } from 'react';
import { useGiftCard } from '../../../../context/contexts';
import AddGiftCardModal from './addGiftCardModal/AddGiftCardModal';
import InvoiceModal from './giftCardInvoice/invoiceModal/InvoiceModal';
import './GiftCardsManagement.css';

const GiftCardsManagement = () => {
    const {
        giftCards,
        purchaseHistory,
        loading,
        error,
        updateGiftCard,
        addGiftCard,
        deleteGiftCard,
        updatePurchaseStatus
    } = useGiftCard();

    const [activeTab, setActiveTab] = useState('manage');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [editingCard, setEditingCard] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const handleAddCard = () => {
        setEditingCard(null);
        setIsModalOpen(true);
    };

    const handleEditCard = (card) => {
        setEditingCard(card);
        setIsModalOpen(true);
    };

    const handleDeleteCard = async (carTypeIndex) => {
        if (window.confirm('Are you sure you want to delete this gift card?')) {
            const cardType = giftCards[carTypeIndex].carType;
            await deleteGiftCard(cardType);
        }
    };

    const handleSaveCard = async (cardData) => {
        if (editingCard) {
            await updateGiftCard(cardData);
        } else {
            await addGiftCard(cardData);
        }
        setIsModalOpen(false);
        setEditingCard(null);
    };

    const handleStatusChange = async (purchaseId, newStatus) => {
        await updatePurchaseStatus(purchaseId, newStatus);
    };

    const handleViewDetails = (purchase) => {
        setSelectedPurchase(purchase);
        setIsInvoiceModalOpen(true);
    };

    const filteredPurchaseHistory = purchaseHistory.filter((purchase) => {
        const matchesSearch = 
            purchase.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            purchase.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || purchase.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    return (
        <div className="gift-cards-management">
            <div className="gift-cards-management__header">
                <h2>Gift Cards Management</h2>
                <div className="gift-cards-management__tabs">
                    <button 
                        className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manage')}
                    >
                        Manage Gift Cards
                    </button>
                    <button 
                        className={`tab ${activeTab === 'purchases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('purchases')}
                    >
                        Purchase History
                    </button>
                </div>
            </div>

            <div className="gift-cards-management__content">
                {activeTab === 'manage' ? (
                    <div className="gift-cards-management__cards">
                        <div className="gift-cards-management__actions">
                            <button className="add-card-btn" onClick={handleAddCard}>
                                Add New Gift Card
                            </button>
                        </div>

                        <div className="gift-cards-list">
                            {giftCards.map((carType, index) => (
                                <div key={index} className="gift-card-item">
                                    <div className="gift-card-item__header">
                                        <h3>{carType.carType}</h3>
                                        <div className="gift-card-item__actions">
                                            <button onClick={() => handleEditCard(carType)}>Edit</button>
                                            <button onClick={() => handleDeleteCard(index)}>Delete</button>
                                        </div>
                                    </div>
                                    <div className="gift-card-item__services">
                                        {carType.services.map((service, serviceIndex) => (
                                            <div key={serviceIndex} className="service-item">
                                                <h4>{service.name}</h4>
                                                <p>${service.price}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="gift-cards-management__purchases">
                        <div className="purchase-history-filters">
                            <input
                                type="text"
                                placeholder="Search by name or email"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="used">Used</option>
                                <option value="expired">Expired</option>
                            </select>
                        </div>

                        <table className="purchase-history-table">
                            <thead>
                                <tr>
                                    <th>Purchase Date</th>
                                    <th>User Type</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Gift Card</th>
                                    <th>Price</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPurchaseHistory.map((purchase) => (
                                    <tr key={purchase.id}>
                                        <td>{purchase.purchaseDate}</td>
                                        <td>{purchase.userType}</td>
                                        <td>{purchase.userName}</td>
                                        <td>{purchase.email}</td>
                                        <td>{`${purchase.giftCard.carType} - ${purchase.giftCard.service}`}</td>
                                        <td>${purchase.giftCard.price}</td>
                                        <td>
                                            <select
                                                value={purchase.status}
                                                onChange={(e) => handleStatusChange(purchase.id, e.target.value)}
                                                className={`status-select status-${purchase.status}`}
                                            >
                                                <option value="active">Active</option>
                                                <option value="used">Used</option>
                                                <option value="expired">Expired</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button onClick={() => handleViewDetails(purchase)}>
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <AddGiftCardModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingCard(null);
                }}
                onSave={handleSaveCard}
                editingCard={editingCard}
            />

            <InvoiceModal
                isOpen={isInvoiceModalOpen}
                onClose={() => {
                    setIsInvoiceModalOpen(false);
                    setSelectedPurchase(null);
                }}
                purchase={selectedPurchase}
            />
        </div>
    );
};

export default GiftCardsManagement;